import { Injectable, type TemplateRef, inject } from '@angular/core';
import { DialogService } from '../dialog';
import type {
    DuplicateDialogConfig,
    DuplicateDialogContext,
    DuplicateDialogOptions,
    FileUploadIssue,
} from './models';

const DEFAULT_DISALLOW_DUPLICATE_DIALOG: DuplicateDialogOptions = {
    title: 'Duplicates detected',
    text1: 'The following documents have not been uploaded because they already exist on file.',
    confirmButton: 'OK',
};

@Injectable()
export class Feedback {
    //#region Dependency injection
    private readonly dialogService = inject(DialogService);
    //#endregion

    //#region Public methods
    public async showDuplicateDialog(
        config: DuplicateDialogConfig
    ): Promise<void> {
        const dialogRef = this.dialogService.openConfirmationDialog(
            this.resolveDuplicateDialogConfig(config),
            { disableClose: false }
        );

        await dialogRef.afterClosed().toPromise();
    }

    public showValidationIssues(issues: readonly FileUploadIssue[]): void {
        if (issues.length === 0) return;

        if (issues.length === 1) {
            const issue = issues[0];

            this.dialogService.openConfirmationDialog({
                title: issue.title,
                text1: issue.message,
                text2: this.toRejectedFilesText(issue.rejectedFiles),
                status: 'destructive',
            });
            return;
        }

        const combinedText = issues
            .map((issue) =>
                [
                    issue.title,
                    issue.message,
                    this.toRejectedFilesText(issue.rejectedFiles),
                ]
                    .filter(Boolean)
                    .join('\n\n')
            )
            .join('\n\n');

        this.dialogService.openConfirmationDialog({
            title: 'File validation errors',
            text1: combinedText,
            status: 'destructive',
        });
    }
    //#endregion

    //#region Private methods
    private resolveDuplicateDialogConfig(config: DuplicateDialogConfig) {
        return {
            ...DEFAULT_DISALLOW_DUPLICATE_DIALOG,
            ...config.dialogOptions,
            text2: config.dialogTemplate
                ? ''
                : config.duplicateNames.map((name) => `• ${name}`).join('\n'),
            contentTpl: config.dialogTemplate as
                | TemplateRef<DuplicateDialogContext>
                | undefined,
            contentTplContext: config.dialogTemplate
                ? {
                      clientDuplicates: config.duplicateNames,
                      ...(config.dialogTemplateContext ?? {}),
                  }
                : undefined,
        };
    }

    private toRejectedFilesText(
        rejectedFiles: readonly { name: string; size: number }[]
    ): string {
        if (rejectedFiles.length === 0) return '';

        return [
            'The following files will not be uploaded:',
            '',
            ...rejectedFiles.map((file) => `• ${file.name}`),
        ].join('\n');
    }
    //#endregion
}
